import { useState } from 'react';
import type { Demande, Statut } from '../../types';
import { formatDateCourt } from './format';
import { joursAvant } from './urgence';

/**
 * Séances à couvrir d'urgence (vue gestion) : une séance dans les N prochains jours — N = le seuil « Urgent » déjà
 * réglable dans Administration (calendrier.urgence_urgent_max_jours) — qui n'a PAS encore de remplaçant·e : brouillon,
 * demande envoyée ou recherche en cours, y compris les absences repérées dans Tipee.
 * Pur affichage : la liste est calculée à partir des demandes déjà chargées par le tableau de bord.
 */
export const STATUTS_A_COUVRIR: Statut[] = [
  'brouillon', 'soumise', 'recherche_interne_1', 'recherche_interne_2', 'recherche_externe', 'urgence_cascade',
];

/** Les séances à couvrir dans les `jours` prochains jours, la plus proche d'abord. */
export function seancesACouvrir(demandes: Demande[], aujourdhui: Date, jours: number): Demande[] {
  return demandes
    .filter((d) => STATUTS_A_COUVRIR.includes(d.statut) && !d.remplacant_nom && !d.deleted_at)
    .filter((d) => { const j = joursAvant(d.date_atelier, aujourdhui); return j >= 0 && j <= jours; })
    .sort((a, b) => a.date_atelier.localeCompare(b.date_atelier) || (a.atelier_nom ?? '').localeCompare(b.atelier_nom ?? '', 'fr'));
}

function quand(dateISO: string, aujourdhui: Date): string {
  const j = joursAvant(dateISO, aujourdhui);
  if (j === 0) return "Aujourd'hui";
  if (j === 1) return 'Demain';
  return formatDateCourt(dateISO);
}

/** Où en est la séance, en un mot — la couleur propre à Tipee pour une absence repérée et pas encore envoyée. */
function etat(d: Demande): { texte: string; classe: string } {
  switch (d.statut) {
    case 'brouillon': return d.source === 'tipee' ? { texte: 'Tipee · pas envoyée', classe: 'pastille pastille-tipee' } : { texte: 'Brouillon', classe: 'pastille pastille-brouillon' };
    case 'soumise': return { texte: 'Envoyée · recherche à lancer', classe: 'pastille pastille-recherche' };
    case 'recherche_interne_1': return { texte: 'Recherche · 1er appel', classe: 'pastille pastille-recherche' };
    case 'recherche_interne_2': return { texte: 'Recherche · relance', classe: 'pastille pastille-recherche' };
    case 'recherche_externe': return { texte: 'Recherche élargie', classe: 'pastille pastille-recherche' };
    default: return { texte: 'Urgence en cours', classe: 'pastille pastille-urgence' };
  }
}

const NB_VISIBLES = 5;

export function BandeauUrgences({ seances, jours, aujourdhui, onOuvrir }: {
  seances: Demande[]; jours: number; aujourdhui: Date; onOuvrir: (id: number) => void;
}) {
  const [toutVoir, setToutVoir] = useState(false);
  const n = seances.length;

  // Rien d'urgent : le bandeau garde sa place, en vert — la page ne saute pas quand la dernière urgence est réglée,
  // et l'on sait qu'on n'a rien raté.
  if (n === 0) {
    return (
      <div className="bandeau-calme mb-16" role="status">
        ✓ Aucune séance à couvrir d'urgence dans les {jours} prochains jours
      </div>
    );
  }

  const nbTipee = seances.filter((d) => d.statut === 'brouillon' && d.source === 'tipee').length;
  const visibles = toutVoir ? seances : seances.slice(0, NB_VISIBLES);
  return (
    <section className="bandeau-urgence mb-16" aria-labelledby="bandeau-urgence-titre">
      <div className="bandeau-urgence-tete">
        <span className="bandeau-urgence-gros" aria-hidden="true">{n}</span>
        <h2 id="bandeau-urgence-titre" className="bandeau-urgence-titre">
          <span className="sr-only">{n} </span>séance{n > 1 ? 's' : ''} à couvrir d'urgence
        </h2>
        <span className="bandeau-urgence-sous">
          dans les {jours} jours, sans remplaçant·e{nbTipee > 0 ? ` · dont ${nbTipee} repérée${nbTipee > 1 ? 's' : ''} dans Tipee` : ''}
        </span>
      </div>
      <ul className="bandeau-urgence-lignes">
        {visibles.map((d) => {
          const e = etat(d);
          return (
            <li key={d.id} className="bandeau-urgence-ligne">
              <span className="bandeau-urgence-quand">{quand(d.date_atelier, aujourdhui)}</span>
              <span className="bandeau-urgence-qui">
                <b>{d.atelier_nom}</b> · {d.absent_nom} · <span className={e.classe}>{e.texte}</span>
              </span>
              <button type="button" className="btn btn-sm" onClick={() => onOuvrir(d.id)}
                aria-label={`Ouvrir la demande : ${d.atelier_nom}, ${quand(d.date_atelier, aujourdhui)}`}>Ouvrir</button>
            </li>
          );
        })}
      </ul>
      {n > NB_VISIBLES && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setToutVoir((v) => !v)} aria-expanded={toutVoir}>
          {toutVoir ? 'Réduire la liste' : `Voir les ${n - NB_VISIBLES} autres`}
        </button>
      )}
    </section>
  );
}
